import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import CustomInput from '../widgets/CustomInput';
import AppContext from '../context/AppContext';
import { HeadingContext } from '../context/HeadingContext';
import { fetchUserProfile, updateUserProfile, updateEmail } from '../_slice/profile';
import { toast } from 'react-toastify';
import CustomModal from '../widgets/CustomModal';
import CustomBtn from '../widgets/CustomBtn';

function Profile() {
    const { setLoader } = useContext(AppContext);
    const { setHeading } = useContext(HeadingContext);

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    // State for modal and form data
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [pendingProfileData, setPendingProfileData] = useState(null);
    const [initialEmail, setInitialEmail] = useState('');

    // const profileData = useSelector(state => state.user.profileData || {});

    const profileData = useSelector(
        state => state.user.profileData || {},
        shallowEqual
    );

    const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
        mode: 'onChange',
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone: ''
        }
    });

    const {
        register: registerToken,
        handleSubmit: handleSubmitToken,
        formState: { errors: errorsToken },
        reset: resetToken
    } = useForm({
        mode: "onChange",
        defaultValues: {
            token: "",
        },
    });

    // Watch email field
    const currentEmail = watch('email');

    useEffect(() => {
        dispatch(fetchUserProfile({ navigate, setLoader }));
    }, [dispatch, navigate, setLoader]);

    useEffect(() => {
        setHeading("Profile");
    }, [setHeading, location?.state?.orgId]);

    useEffect(() => {
        if (profileData && Object.keys(profileData).length > 0) {
            setInitialEmail(profileData.email || '');
            reset({
                first_name: profileData.first_name || '',
                last_name: profileData.last_name || '',
                email: profileData.email || '',
                phone: profileData.phone || ''
            });
        }
    }, [profileData, reset]);

    const handleInput = (event) => {
        event.target.value = event.target.value.replace(/[^0-9]/g, '');
    };

    const handleEmailUpdate = (email, formData) => {
        setNewEmail(email);
        setPendingProfileData(formData); // Store all form data when email changes

        dispatch(updateEmail({
            emailData: { email },
            navigate,
            setLoader,
        }))
            .unwrap()
            .then(() => {
                toast.success('Verification token sent to your new email address');
                setShowTokenModal(true);
            })
            .catch((error) => {
                toast.error(error.payload || error.message || 'Failed to send verification token');
            });
    };

    const handleTokenSubmit = (tokenData) => {
        if (!tokenData.token) {
            toast.error('Please enter verification token');
            return;
        }

        // Combine pending profile data with the token
        const updateData = {
            ...pendingProfileData,
            token: tokenData.token
        };

        dispatch(updateUserProfile({
            profileData: updateData,
            navigate,
            setLoader,
        }))
            .unwrap()
            .then((res) => {
                toast.success('Profile updated successfully!');
                setShowTokenModal(false);
                setNewEmail('');
                setPendingProfileData(null);
                // Refresh profile data
                dispatch(fetchUserProfile({ navigate, setLoader }));
            })
            .catch((error) => {
                toast.error(error.payload || error.message || 'Failed to verify email');
            });
    };

    const onSubmitProfile = (data) => {
        // Check if email has changed
        if (data.email && data.email !== initialEmail) {
            handleEmailUpdate(data.email, data);
            return;
        }

        // Regular profile update
        dispatch(updateUserProfile({
            profileData: data,
            navigate,
            setLoader,
        }))
            .unwrap()
            .then((res) => {
                // console.log('res', res);
                toast.success('Profile updated successfully!');
            })
            .catch((error) => {
                toast.error(error.payload || error.message || 'Failed to update profile');
            });
    };

    useEffect(() => {
        if (showTokenModal) {
            // Reset the token form to clear input when modal opens
            resetToken({ token: '' });
        }
    }, [showTokenModal]);


    return (
        <>
            <div className='col-lg-6'>
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h1 className="org_header_heading">Profile</h1>
                </div>

                <form onSubmit={handleSubmit(onSubmitProfile)}>
                    <div className="row">
                        <div className="col-lg-6">
                            <label className='innerlable'>First Name</label>
                            <CustomInput
                                type="text"
                                classname="custom_input"
                                {...register("first_name", {
                                    required: 'First name required'
                                })}
                            />
                            {errors.first_name && <p className='text-danger'>{errors.first_name.message}</p>}
                        </div>
                        <div className="col-lg-6">
                            <label className='innerlable'>Last Name</label>
                            <CustomInput
                                type="text"
                                classname="custom_input"
                                {...register("last_name", {
                                    required: 'Last name required'
                                })}
                            />
                            {errors.last_name && <p className='text-danger'>{errors.last_name.message}</p>}
                        </div>
                        <div className="col-lg-6">
                            <label className='innerlable'>Email</label>
                            <CustomInput
                                type="email"
                                classname="custom_input"
                                {...register("email", {
                                    required: 'Email required',
                                    pattern: {
                                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/i,
                                        message: 'Invalid email address'
                                    }
                                })}
                            />
                            {errors.email && <p className='text-danger'>{errors.email.message}</p>}
                            {profileData?.email && currentEmail !== profileData.email && (
                                <p className="text-danger small mt-1">You'll need to verify your new email address</p>
                            )}
                        </div>
                        <div className="col-lg-6">
                            <label className='innerlable'>Phone Number</label>
                            <CustomInput
                                type="text"
                                classname="custom_input"
                                onInput={handleInput}
                                {...register("phone", {
                                    pattern: {
                                        value: /^[0-9]{10}$/,
                                        message: 'Phone number must be exactly 10 digits'
                                    }
                                })}
                            />
                            {errors.phone && <p className='text-danger'>{errors.phone.message}</p>}
                        </div>
                    </div>
                    <button type="submit" className="submit_btn mt-5">Save</button>
                </form>
            </div>

            {/* Token Verification Modal */}
            <CustomModal
                show={showTokenModal}
                onHide={() => setShowTokenModal(false)}
                title="Verify Your Email"
                body={
                    <form onSubmit={handleSubmitToken(handleTokenSubmit)}>
                        <div className='row'>
                            <div className='col-lg-12'>
                                <div className='form-group'>
                                    <label className='innerlable'>Verification Token</label>
                                    <input
                                        type='text'
                                        className='form-control'
                                        placeholder='Enter verification token'
                                        {...registerToken('token', { required: 'Token is required' })}
                                    />
                                    {errorsToken.token && (
                                        <p className='text-danger'>{errorsToken.token.message}</p>
                                    )}
                                </div>
                            </div>

                            <div className="col-lg-6 mt-4">
                                <CustomBtn
                                    buttonText='Verify'
                                    type='submit'
                                    className='btn-primary'
                                />
                            </div>

                            <div className="col-lg-6 d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => handleEmailUpdate(newEmail, pendingProfileData)}
                                >
                                    Resend Token
                                </button>
                            </div>
                        </div>
                    </form>
                }
            />
        </>
    );
}

export default Profile;